import React, { useEffect, useState, useRef } from 'react';
import { Menu, X, ArrowRight, Instagram, Mail, Check, Star, ChevronDown } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';
import ReCAPTCHA from "react-google-recaptcha";

// --- Global Data ---

const pricingPlans = [
    {
        id: 'starter',
        title: 'Starter Page',
        description: 'Perfect for startups and individuals ready to launch a professional digital footprint.',
        price: '699.00',
        features: [
            'Up to 5 Pages', 'Custom UI/UX', 'Mobile Optimised', 'Basic SEO setup',
            'Contact Form', 'WhatsApp & Social Integration', 'Email Support', 'User Training',
        ],
        popular: false,
    },
    {
        id: 'standard',
        title: 'Growth Plan', 
        description: 'The ideal choice for small businesses seeking to expand their reach and capabilities.',
        price: '999.00',
        features: [
            'Up to 10 Pages', 'Custom UI/UX', 'Mobile Optimised', 'Basic SEO setup',
            'Contact Form', 'WhatsApp & Social Integration', 'Email Support', 'User Training',
        ],
        popular: true,
    },
    {
        id: 'corporate',
        title: 'Corporate Suite',
        description: 'Enterprise-grade solutions for organizations requiring scale and advanced interactivity.',
        price: '1,299.00',
        features: [
            'Up to 15 Pages', 'Custom UI/UX', 'Mobile Optimised', 'Basic SEO setup',
            'Contact Form', 'WhatsApp & Social Integration', 'Email Support', 'Priority Support','User Training',
        ],
        popular: false,
    },
    {
        id: 'ecommerce',
        title: 'E-Commerce',
        description: 'Robust online store solutions designed to drive sales and manage inventory effectively.',
        price: 'Custom', // Indicates custom pricing
        features: [
             'Payment Gateway Setup', 'Shopping Cart Integration', 'Inventory Management',
            'Basic SEO setup', 'WhatsApp & Social Integration', 'Priority Support', 'Admin Dashboard', 'User Training',
        ],
        popular: false,
    },
];

const services = [
    {
      id: 'dev',
      title: 'Website Design and Development',
      link: '/web-development',
    },
    {
      id: 'revamp',
      title: 'Website Revamp',
      link: '/website-revamp',
    },
    {
      id: 'hosting',
      title: 'Server Hosting & Maintenance',
      link: '/hosting-maintenance',
    },
  ];

const FooterSection = () => {
    return (
        <footer className="bg-slate-900/90 backdrop-blur-sm text-slate-300 border-t border-slate-700/50 py-16 px-6">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-12 border-b border-slate-700 pb-12">
                    {/* Column 1: Brand & Contact */}
                    <div className="col-span-2 md:col-span-2 space-y-6">
                        <a href="/" className="flex items-center gap-3">
                            
                            <img src="/logo.png" alt="SR logo" className="w-12 h-12 object-cover rounded-xl" />
                            <div>
                                <div className="text-xl font-bold text-white">Vyxion Labs</div>
                                <div className="text-sm text-slate-400">Digital Presence. Engineered.</div>
                            </div>
                        </a>
                        <p className="text-sm text-slate-400 max-w-xs">
                            We are committed to delivering outstanding digital solutions that combine stunning design with robust performance.
                        </p>
                        
                        <div className="flex space-x-4 pt-2">
                        <a href="https://www.tiktok.com/@vyxion.labs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="TikTok"><FaTiktok size={20} /></a>
                        <a href="https://www.instagram.com/vyxionlabs" className="text-slate-400 hover:text-cyan-500 transition-colors" aria-label="Instagram"><Instagram size={20} /></a>
                            
                        </div>
                    </div>

                    {/* Column 2: Quick Links */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-white mb-4">Quick Links</h4>
                        <ul className="space-y-3">
                            <li><a href="/consult" className="text-sm hover:text-cyan-500 transition-colors">Book a Free Consult</a></li>
                            <li><a href="/pricing" className="text-sm hover:text-cyan-500 transition-colors">Pricing</a></li>
                            <li><a href="/ourworks" className="text-sm hover:text-cyan-500 transition-colors">Our Works</a></li>
                            <li><a href="/faq" className="text-sm hover:text-cyan-500 transition-colors">FAQ</a></li>
                            <li><a href="/about" className="text-sm hover:text-cyan-500 transition-colors">About Us</a></li>
                            
                        </ul>
                    </div>

                    {/* Column 3: Services */}
                    <div className="space-y-4">
                        <h4 className="text-lg font-bold text-white mb-4">Services</h4>
                        <ul className="space-y-3">
                            {services.map((s, index) => (
                                <li key={index}><a href={s.link} className="text-sm hover:text-cyan-500 transition-colors">{s.title}</a></li>
                            ))}
                        </ul>
                    </div>
                    
                    {/* Column 4: Get In Touch */}
                    <div className="space-y-4 col-span-2 md:col-span-1">
                        <h4 className="text-lg font-bold text-white mb-4">Contact</h4>
                        <ul className="space-y-3">
                            <li className="flex items-center gap-3">
                                <Mail size={16} className="text-cyan-500 flex-shrink-0" />
                                <a href="mailto:vyxionlabs.info@gmail.com" className="text-sm hover:text-cyan-500 transition-colors">vyxionlabs.info@gmail.com</a>
                            </li>
                        </ul>
                    </div>
                </div>
                
                {/* --- LEGAL LINKS --- */}
                <div className="flex justify-center md:justify-start space-x-6 mt-8">
                    
                    <a href="/privacy" className="text-xs text-slate-500 hover:text-cyan-500 transition-colors font-medium">Privacy Policy</a>
                </div>
                {/* ------------------- */}

                {/* Copyright */}
                <div className="mt-4 text-center text-sm text-slate-500">
                    &copy; {new Date().getFullYear()} Vyxion Labs. All rights reserved.
                </div>
            </div>
        </footer>
    );
};

const serviceOptions = [
    { label: 'Select a Service', value: '', disabled: true },
    { label: 'Website Design and Development', value: 'Website Design and Development' },
    { label: 'Website Revamp (Redesign)', value: 'Website Revamp (Redesign)' },
    { label: 'E-Commerce Development', value: 'E-Commerce Development' }, // Added Option
];

const communicationOptions = [
    { label: 'WhatsApp', value: 'WhatsApp' },
    { label: 'Email', value: 'Email' },
    { label: 'Phone Call', value: 'Phone Call' },
];

// --- Form Components ---

const InputField = ({ label, name, type, value, onChange, required, placeholder }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-semibold text-white/90">
            {label} {required && <span className="text-red-400">*</span>}
        </label>
        <div className="mt-1">
            <input
                id={name}
                name={name}
                type={type}
                value={value}
                onChange={onChange}
                required={required}
                placeholder={placeholder}
                className="block w-full px-0 py-2 bg-transparent border-0 border-b-2 border-slate-600 text-white placeholder-slate-500 focus:border-cyan-500 focus:ring-0 transition-colors sm:text-base"
            />
        </div>
    </div>
);

const TextareaField = ({ label, name, value, onChange, required, placeholder }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-semibold text-white/90">
            {label} {required && <span className="text-red-400">*</span>}
        </label>
        <div className="mt-1">
            <textarea
                id={name}
                name={name}
                rows="3"
                value={value}
                onChange={onChange}
                required={required}
                placeholder={placeholder}
                className="block w-full px-0 py-2 bg-transparent border-0 border-b-2 border-slate-600 text-white placeholder-slate-500 focus:border-cyan-500 focus:ring-0 transition-colors sm:text-base"
            ></textarea>
        </div>
    </div>
);

const SelectField = ({ label, name, value, onChange, options, required }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-semibold text-white/90">
            {label} {required && <span className="text-red-400">*</span>}
        </label>
        <div className="mt-1 relative">
            <select
                id={name}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                className="block w-full px-0 py-2 bg-transparent border-0 border-b-2 border-slate-600 text-white focus:border-cyan-500 focus:ring-0 transition-colors sm:text-base appearance-none"
            >
                {options.map((option) => (
                    <option key={option.value} value={option.value} disabled={option.disabled} className="bg-slate-800 text-white">
                        {option.label}
                    </option>
                ))}
            </select>
            <ChevronDown className="absolute right-0 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>
    </div>
);

const RadioGroupField = ({ label, name, value, onChange, options, required }) => (
    <fieldset>
        <legend className="block text-sm font-semibold text-white/90 mb-4">
            {label} {required && <span className="text-red-400">*</span>}
        </legend>
        <div className="flex flex-wrap gap-x-8 gap-y-4">
            {options.map((option) => (
                <div key={option.value} className="flex items-center">
                    <input
                        id={option.value}
                        name={name}
                        type="radio"
                        value={option.value}
                        checked={value === option.value}
                        onChange={onChange}
                        required={required}
                        className="focus:ring-cyan-500 h-4 w-4 text-cyan-600 border-slate-600 bg-slate-800"
                    />
                    <label htmlFor={option.value} className="ml-3 block text-base font-medium text-slate-300">
                        {option.label}
                    </label>
                </div>
            ))}
        </div>
    </fieldset>
);

export default function PricingPage() {
    // --- Header & Navigation State (From FreeConsultForm) ---
    const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);
    const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
    const servicesDropdownRef = useRef(null); 
    
    // --- Pricing Page State ---
    const [visible, setVisible] = useState({});
    
    // --- Form State ---
    const formSectionRef = useRef(null);
    const recaptchaRef = useRef();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        contactNumber: '',
        companyName: '',
        service: '',
        preferredCommunication: '',
        projectDetails: '',
    });
    const [status, setStatus] = useState('');

    // --- Header Handlers (From FreeConsultForm) ---
    const handleServicesClick = (e) => {
        e.stopPropagation(); 
        setIsServicesDropdownOpen(!isServicesDropdownOpen);
    };
    
    const handleMenuClick = () => {
        setIsContactPopupOpen(!isContactPopupOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
                setIsServicesDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // --- Pricing Page Effects ---
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) setVisible((v) => ({ ...v, [e.target.id]: true }));
                });
            },
            { threshold: 0.1 }
        );
        document.querySelectorAll('[data-observe]').forEach((el) => observer.observe(el));
        
        return () => {
            observer.disconnect();
        };
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePlanSelection = (planTitle) => {
        setFormData(prev => ({
            ...prev,
            projectDetails: `I am interested in the ${planTitle} package and would like to find out more.`
        }));
        formSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const captchaToken = recaptchaRef.current.getValue();
        if (!captchaToken) {
            alert('Please complete the captcha verification');
            return;
        }

        setStatus('loading');
        try {
            const response = await fetch('https://submitform-3dhcjoli2q-uc.a.run.app', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, captchaToken }),
            });

            if (response.ok) {
                setStatus('success');
                setFormData({ name: '', email: '', contactNumber: '', companyName: '', service: '', preferredCommunication: '', projectDetails: '' });
                recaptchaRef.current.reset();
            } else {
                setStatus('error');
            }
        } catch (error) {
            setStatus('error');
        }
    };

    return (
        <div className="bg-white text-neutral-900 min-h-screen antialiased relative overflow-x-hidden">
            {/* --- Styles from FreeConsultForm --- */}
            <style>{`
                @keyframes shimmer {
                0% { background-position: -1000px 0; }
                100% { background-position: 1000px 0; }
                }
                .shimmer {
                background: linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent);
                background-size: 1000px 100%;
                animation: shimmer 3s infinite;
                }
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0, 0, 0, 0.4);
                    backdrop-filter: blur(8px);
                    z-index: 9999; 
                    opacity: 0;
                    visibility: hidden;
                    transition: opacity 0.3s ease, visibility 0.3s ease;
                }
                .modal-overlay.open {
                    opacity: 1;
                    visibility: visible;
                }
                .modal-content {
                    position: fixed;
                    top: 0;
                    right: 0;
                    width: 90%;
                    max-width: 400px;
                    height: 100%;
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(30px);
                    box-shadow: -10px 0 30px rgba(0, 0, 0, 0.2);
                    transform: translateX(100%);
                    transition: transform 0.4s cubic-bezier(0.86, 0, 0.07, 1);
                    overflow-y: auto;
                    padding: 3rem 1.5rem;
                }
                .modal-overlay.open .modal-content {
                    transform: translateX(0);
                }
            `}</style>

            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,0.03)_1px,transparent_1px)] bg-[size:100px_100px]" />
            </div>

            {/* --- NEW HEADER (From FreeConsultForm) --- */}
            <header className="w-full z-[100] border-b border-neutral-200 bg-white sticky top-0">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                <div className="flex items-center justify-between h-20">
                    {/* Logo */}
                    <a href="/" className="flex items-center gap-3 z-50 opacity-100 fill-mode-forwards flex-shrink-0">
                    <div className="relative w-10 h-10 md:w-14 md:h-14 flex items-center justify-center">
                        <img src="/logo.png" alt="SR logo" className="w-full h-full object-cover rounded-sm" />
                    </div>
                    <div>
                        <div className="text-lg font-bold text-neutral-900">Vyxion Labs</div>
                        <div className="text-[10px] md:text-xs text-neutral-600 font-medium">Digital Presence. Engineered.</div>
                    </div>
                    </a>

                    {/* Nav - Center - Flexbox Layout */}
                    <nav className="hidden lg:flex flex-1 justify-center items-center gap-6 xl:gap-8  opacity-100 fill-mode-forwards px-4" style={{animationFillMode: 'forwards'}}>
                        <a href="/" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">Home</a>
                        <div className="relative" ref={servicesDropdownRef}>
                            <span id="services-link" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium cursor-pointer" onClick={handleServicesClick}>Services</span>
                            {isServicesDropdownOpen && (
                        <div id="services-dropdown" className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-64 bg-white rounded-sm border border-neutral-200 overflow-hidden z-[110]">
                        <div className="flex flex-col p-2">
                            <a href="/web-development" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm bg-neutral-100 transition-colors text-sm font-bold text-neutral-900">
                                Website Design & Development
                            </a>
                            <a href="/website-revamp" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                                Website Revamp
                            </a>
                            <a href="/hosting-maintenance" onClick={() => setIsServicesDropdownOpen(false)} className="p-3 block rounded-sm hover:bg-neutral-100 transition-colors text-sm font-medium text-neutral-800">
                                Server Hosting & Maintenance
                            </a>
                        </div>
                    </div>
                    
                            )}
                        </div>
                        <a href="/pricing" className="text-neutral-900 font-bold cursor-pointer">Pricing</a>
                        <a href="/about" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">About Us</a>
                        <a href="/ourworks" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">Our Works</a>
                        <a href="/faq" className="text-neutral-700 hover:text-cyan-600 transition-colors font-medium">FAQ</a>
                    </nav>

                    {/* Right Controls */}
                    <div className="flex items-center gap-3 md:gap-4 opacity-100 fill-mode-forwards flex-shrink-0" style={{animationFillMode: 'forwards'}}>
                        <a href="/consult" className="hidden lg:flex group relative px-5 py-2 bg-gray-800 text-gray-200 font-semibold rounded-md hover:bg-gray-900 transition-all text-sm shadow-sm">
                        <span className="relative z-10 flex items-center gap-2 whitespace-nowrap">Free Consult <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                        </a>
                        <div className="flex items-center gap-3 border-l border-neutral-200 pl-3 md:pl-4">
                            <a href="https://www.tiktok.com/@vyxion.labs" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-black transition-colors"><FaTiktok className="w-4 h-4" /></a>
                            <a href="https://www.instagram.com/vyxionlabs" target="_blank" rel="noopener noreferrer" className="text-neutral-600 hover:text-pink-600 transition-colors"><Instagram className="w-4 h-4" /></a>
                        </div>
                        <button className="lg:hidden flex items-center gap-1 px-3 py-2 text-neutral-700 font-semibold rounded-sm border border-neutral-300 hover:bg-neutral-100 transition-all text-sm bg-white ml-2" onClick={handleMenuClick}>
                            <Menu className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                </div>
            </header>
            
            {/* Contact Overlay */}
            <div className={`modal-overlay ${isContactPopupOpen ? 'open' : ''}`} onClick={handleMenuClick}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <button className="absolute top-6 right-6 p-2 rounded-sm bg-neutral-100 hover:bg-neutral-200 transition-colors" onClick={handleMenuClick}>
                        <X size={24} className="text-neutral-700" />
                    </button>
                    <h2 className="text-3xl font-bold text-neutral-900 mb-8 mt-12 animate-slideInLeft">Navigation</h2>
                    <div className="space-y-4 mb-10 border-b border-neutral-200 pb-8">
                        <div className="space-y-3 pl-4 border-l-2 border-purple-200 animate-slideInLeft delay-100">
                            <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider">Services</p>
                            <a href="/web-development" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Web Development</a>
                            <a href="/website-revamp" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Website Revamp</a>
                            <a href="/hosting-maintenance" className="block text-base font-medium text-neutral-600 hover:text-cyan-500" onClick={handleMenuClick}>Hosting</a>
                        </div>
                        <a href="/pricing" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-200" onClick={handleMenuClick}>Pricing</a>
                        <a href="/" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-200" onClick={handleMenuClick}>Home</a>
                        <a href="/faq" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-300" onClick={handleMenuClick}>FAQ</a>
                        <a href="/about" className="block text-2xl font-bold text-neutral-800 hover:text-cyan-500 animate-slideInLeft delay-400" onClick={handleMenuClick}>About Us</a>
                        <a href="/consult" className="group relative block w-full text-center mt-12 px-6 py-4 bg-gray-800 text-gray-200 font-bold rounded-md hover:bg-gray-700 transition-all text-sm" onClick={handleMenuClick}>
                    <span className="relative z-10 flex items-center justify-center gap-2">Start a Project Today <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></span>
                    </a>
                    </div>
                    <h3 className="text-2xl font-bold text-neutral-900 mb-4">Get in Touch</h3>
                    <div className="space-y-4 mb-10">
                        <div className="flex items-start gap-4 p-4 rounded-sm border border-neutral-200 bg-neutral-50">
                            <Mail size={24} className="text-cyan-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <div className="text-lg font-bold text-neutral-800">Email</div>
                                <a href="mailto:vyxionlabs.info@gmail.com" className="text-neutral-600 hover:text-cyan-500 transition-colors text-base break-all">vyxionlabs.info@gmail.com</a>
                            </div>
                        </div>
                    </div>
                    
                </div>
            </div>

            <main className="pt-12">
                {/* Pricing Plans Section */}
                <section className="py-12 md:py-0 px-4">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16 space-y-4">
                            <h1 className="text-4xl md:text-6xl font-black tracking-tight text-neutral-900">Pricing</h1>
                            <p className="text-lg text-neutral-500 max-w-2xl mx-auto font-medium">Choose the perfect plan for your business journey.</p>
                        </div>

                        {/* Updated Grid to support 4 items responsive */}
                        <div className="flex flex-wrap gap-8 items-stretch justify-center">
                            {pricingPlans.map((plan, index) => (
                                <div 
                                    key={plan.id}
                                    id={`plan-${index}`}
                                    data-observe
                                    className={`relative flex flex-col w-full md:w-[48%] xl:w-[23%] p-8 rounded-3xl border transition-all duration-500 
                                        ${plan.popular ? 'bg-slate-900 text-white shadow-2xl scale-100 md:scale-105 z-10' : 'bg-white border-neutral-200 text-neutral-900 shadow-lg'}
                                        ${visible[`plan-${index}`] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                                >
                                    {plan.popular && (
                                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-cyan-500 text-white text-xs font-black px-4 py-1.5 rounded-full uppercase tracking-widest flex items-center gap-1.5">
                                            <Star size={12} fill="white" /> Most Popular
                                        </div>
                                    )}
                                    <h3 className={`text-xl font-bold mb-2 ${plan.popular ? 'text-cyan-400' : 'text-neutral-900'}`}>{plan.title}</h3>
                                    
                                    {/* Updated Price Display Logic */}
                                    <div className="flex items-baseline gap-1 mb-4 h-14">
                                        {plan.price === 'Custom' ? (
                                            <span className="text-4xl font-black">Custom</span>
                                        ) : (
                                            <>
                                                <span className="text-sm font-bold opacity-60">SGD</span>
                                                <span className="text-5xl font-black">{plan.price}</span>
                                            </>
                                        )}
                                    </div>

                                    <p className={`text-sm font-medium mb-8 ${plan.popular ? 'text-slate-400' : 'text-neutral-500'}`}>{plan.description}</p>
                                    <div className="flex-grow space-y-4 mb-8">
                                        {plan.features.map((f, i) => (
                                            <div key={i} className="flex items-start gap-3">
                                                <div className={`mt-1 p-0.5 rounded-full ${plan.popular ? 'bg-slate-800' : 'bg-cyan-50'}`}>
                                                    <Check className={`w-3.5 h-3.5 ${plan.popular ? 'text-cyan-400' : 'text-cyan-600'}`} strokeWidth={3} />
                                                </div>
                                                <span className="text-sm font-bold">{f}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <button 
                                        onClick={() => handlePlanSelection(plan.title)}
                                        className={`w-full py-4 rounded-xl font-black transition-all flex items-center justify-center gap-2 group 
                                        ${plan.popular ? 'bg-cyan-500 text-white hover:bg-cyan-400' : 'bg-neutral-900 text-white hover:bg-neutral-800'}`}
                                    >
                                        Get Started <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* --- Integrated Contact Form Section --- */}
                <section ref={formSectionRef} className="relative py-24 px-6 overflow-hidden bg-neutral-50 scroll-mt-24">
                    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 shadow-2xl rounded-[2.5rem] bg-white overflow-hidden border border-neutral-100">
                        <div className="p-10 md:p-16 space-y-8 flex flex-col justify-center bg-white">
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-50 text-cyan-700 text-xs font-black uppercase tracking-widest">
                                Contact Us
                            </div>
                            <h2 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight">Book Your Free Consult</h2>
                            <p className="text-lg text-slate-500 font-medium leading-relaxed">
                                Thinking of getting a website? Put your mind at ease and get a free consult and be one step closer to digitalising your business!
                            </p>
                            <div className="flex items-center gap-4 pt-6">
                                <div className="w-12 h-12 rounded-2xl bg-cyan-50 flex items-center justify-center">
                                    <Mail className="w-6 h-6 text-cyan-600" />
                                </div>
                                <div>
                                    <div className="text-xs font-black text-neutral-400 uppercase tracking-widest">Email Us</div>
                                    <a href="mailto:vyxionlabs.info@gmail.com" className="text-lg font-bold text-slate-800 hover:text-cyan-600 transition-colors">vyxionlabs.info@gmail.com</a>
                                </div>
                            </div>
                        </div>

                        <div className="p-10 md:p-16 bg-slate-900">
                            <h2 className="text-2xl font-black text-white mb-8">Start Your Project</h2>
                            <form onSubmit={handleSubmit} className="space-y-8">
                                <InputField label="Your Full Name" name="name" type="text" value={formData.name} onChange={handleChange} required placeholder="How should we address you?" />
                                <InputField label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="Your email" />
                                <InputField label="Contact Number" name="contactNumber" type="tel" value={formData.contactNumber} onChange={handleChange} required placeholder="For immediate chats" />
                                <InputField label="Company Name" name="companyName" type="text" value={formData.companyName} onChange={handleChange} required placeholder="Your business name." />
                                <SelectField label="Service Interested In" name="service" value={formData.service} onChange={handleChange} options={serviceOptions} required />
                                <RadioGroupField label="Preferred Communication" name="preferredCommunication" value={formData.preferredCommunication} onChange={handleChange} options={communicationOptions} required />
                                <TextareaField label="Tell us about your project" name="projectDetails" value={formData.projectDetails} onChange={handleChange} placeholder="Share some details about your project." />
                                
                                <ReCAPTCHA ref={recaptchaRef} sitekey="6LdAcTUsAAAAAAlRep19XjiGanoxdJhCpkQCjXV3" theme="dark" />
                                
                                {status === 'success' && <div className="p-4 text-sm font-bold text-green-400 bg-green-500/10 rounded-xl border border-green-500/20">✅ Thank you! Request sent successfully.</div>}
                                {status === 'error' && <div className="p-4 text-sm font-bold text-red-400 bg-red-500/10 rounded-xl border border-red-500/20">❌ An error occurred. Please try again.</div>}

                                <button
                                    type="submit"
                                    disabled={status === 'loading'}
                                    className={`w-full py-4 rounded-xl font-black transition-all shadow-xl flex items-center justify-center gap-2 ${status === 'loading' ? 'bg-slate-700 text-slate-400' : 'bg-cyan-400 hover:bg-cyan-300 text-slate-900'}`}
                                >
                                    {status === 'loading' ? 'Submitting...' : <>Submit Request <ArrowRight className="w-5 h-5" /></>}
                                </button>
                            </form>
                        </div>
                    </div>
                </section>

                <FooterSection />
            </main>
        </div>
    );
}